import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { IoLocationSharp } from "react-icons/io5";
import {
  MdAccessTime,
  MdClose,
  MdOutlineAccessTime,
  MdOutlineCalendarMonth,
} from "react-icons/md";
import {
  PiCheckCircleFill,
  PiCircleFill,
  PiPencilSimpleLine,
  PiPlayFill,
  PiStopFill,
  PiTrash,
} from "react-icons/pi";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import activeStatusIcon from "../../assets/images/Active_icon.svg";
import activeLoadStatusIcon from "../../assets/images/Active_load.svg";
import delayedStatsIcon from "../../assets/images/Delayed.svg";
import inactiveStatusIcon from "../../assets/images/Inactive_icon.svg";
import inTransitLoadStatusIcon from "../../assets/images/In_Transit_load.svg";
import inTransitStatsIcon from "../../assets/images/In Transit.svg";
import prospectStatusIcon from "../../assets/images/Prospect_icon.svg";
import totalLoadsStatsIcon from "../../assets/images/Total Loads.svg";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import Input from "../../components/input";
import MultiLineText from "../../components/MultiLineText";
import NewTableComponent from "../../components/NewTableComponent";
import Stats from "../../components/Stats";
import ConfirmDeleteModal from "../../components/modals/ConfirmDeleteModal";
import {
  fetchLoadBookingOverview,
  fetchLoadBookingsPaginated,
} from "../../store/Slices/LoadBookingSlice";
import { fetchTransporterCitiesDropdown } from "../../store/Slices/dropdownSlice";
import "../../styles/LoadBooking.css";
import "../../styles/Table.css";
import "../../styles/TransportersTable.css";
import "../../styles/Trip.css";
import { dateMonthYear, formatUtcDateTimeForInput, formats, modules } from "../../utils/helper";
import {
  activeStatsIcon,
  addTripIcon,
  lateStatsIcon,
  statsCompletedIcon,
} from "../../utils/svgs";
import { successNotify } from "../../utils/toast";
import { locationOptions, statusFilterOptions } from "./loadBooking/loadBooking.constants";
import {
  normalize,
  percent,
  safeArray,
  titleCase,
} from "./loadBooking/loadBooking.helpers";

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const toStatusKey = (status) => normalize(status).replace(/[\s-]+/g, "_");
const toText = (value) => String(value ?? "").trim();
const toLabel = (item) =>
  String(
    item?.label ??
      item?.name ??
      item?.title ??
      item?.city ??
      item?.location ??
      item?.address ??
      item?.value ??
      item ??
      ""
  ).trim();
const toPositiveNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const toDateOnly = (value) => {
  const text = toText(value);
  if (!text) return "";
  return text.split("T")[0];
};
const toDateTimeLocal = (value) => {
  return formatUtcDateTimeForInput(value);
};

const addLocationLookupValue = (map, key, label) => {
  const textKey = toText(key);
  const textLabel = toText(label);
  if (!textKey || !textLabel) return;

  map.set(textKey, textLabel);
  map.set(normalize(textKey), textLabel);
};

const buildLocationLookup = (options = []) => {
  const map = new Map();

  safeArray(options).forEach((option, index) => {
    const label = toLabel(option);
    if (!label) return;

    addLocationLookupValue(map, option?.id, label);
    addLocationLookupValue(map, option?.recordId, label);
    addLocationLookupValue(map, option?.value, label);
    addLocationLookupValue(map, option?.city_id, label);
    addLocationLookupValue(map, option?.location_id, label);
    addLocationLookupValue(map, option?.warehouse_id, label);
    addLocationLookupValue(map, label, label);
    addLocationLookupValue(map, index + 1, label);
  });

  return map;
};

const resolveLocationLookupLabel = (locationLookup, value) => {
  const textValue = toText(value);
  if (!textValue) return "";

  return (
    locationLookup?.get(textValue) ||
    locationLookup?.get(normalize(textValue)) ||
    ""
  );
};

const resolveWarehouseLabel = (stop, locationLookup = new Map()) => {
  const explicitName = toText(
    stop?.customer_warehouse?.warehouse_name ||
      stop?.customer_warehouse?.name ||
      stop?.warehouse?.warehouse_name ||
      stop?.warehouse?.name ||
      stop?.location_name ||
      stop?.point ||
      stop?.address
  );

  const explicitLookupLabel = resolveLocationLookupLabel(locationLookup, explicitName);
  if (explicitLookupLabel) return explicitLookupLabel;

  if (explicitName && Number.isNaN(Number(explicitName))) return explicitName;

  const warehouseId = Number(stop?.customer_warehouse_id ?? stop?.warehouse_id);
  if (Number.isFinite(warehouseId) && warehouseId > 0) {
    return (
      resolveLocationLookupLabel(locationLookup, warehouseId) ||
      (explicitName ? `Warehouse #${explicitName}` : `Warehouse #${warehouseId}`)
    );
  }

  return explicitName || "-";
};

const resolveLoadStopPoint = (stops, targetType, locationLookup) => {
  const normalizedType = normalize(targetType);
  const matchedStop = safeArray(stops).find(
    (stop) => normalize(stop?.stop_type) === normalizedType
  );

  if (matchedStop) return resolveWarehouseLabel(matchedStop, locationLookup);
  return "-";
};

const buildLoadBookingSearchFilters = (filters = {}) => {
  const mapped = {};

  Object.entries(filters || {}).forEach(([filterName, filterObj]) => {
    const rawValue = filterObj?.value;
    const hasNoValue =
      rawValue === undefined ||
      rawValue === null ||
      rawValue === "" ||
      (Array.isArray(rawValue) && rawValue.length === 0);

    if (!filterObj?.filter_type || hasNoValue) return;

    let filterType = filterObj.filter_type;
    let filterValue = rawValue;

    if (filterType === "equal_to") filterType = "equals";

    if (filterType === "one_of") {
      const values = Array.isArray(filterValue) ? filterValue : [filterValue];
      if (values.length === 1) {
        filterType = "equals";
        filterValue = values[0];
      }
    }

    mapped[filterName] = {
      filter_type: filterType,
      value: filterValue,
    };
  });

  return mapped;
};

const toLoadBookingRow = (item, index, locationLookup) => {
  const fallbackId = item?.id ?? index + 1;
  const loadId = toText(item?.load_id) || `TL-${fallbackId}`;
  const loadStops = safeArray(item?.load_stops);
  const loadingPoint = resolveLoadStopPoint(loadStops, "loading", locationLookup);
  const offloadingPoint = resolveLoadStopPoint(
    loadStops,
    "offloading",
    locationLookup
  );
  const expectedDeliveryDate = toDateOnly(item?.delivered_at);
  const notifyConfig = safeArray(item?.load_notify)[0] || {};

  return {
    id: loadId,
    recordId: item?.id ?? fallbackId,
    customerName: toText(item?.customer?.name) || "-",
    transporterName: toText(item?.transporter?.name) || "",
    loadingPoint,
    offloadingPoint,
    expectedDeliveryDate: expectedDeliveryDate || "-",
    status: toText(item?.status) || "inactive",
    attachmentsCount: safeArray(item?.files).length,
    updatedAt: item?.updated_at || item?.created_at || null,
    formData: {
      loadId,
      customerName: toText(item?.customer?.name),
      transporterName: toText(item?.transporter?.name),
      loadingPoint,
      offloadingPoint,
      departureDateTime: toDateTimeLocal(item?.departure_at),
      deliveryDateTime: toDateTimeLocal(item?.delivered_at),
      expectedDeliveryDate,
      notes: toText(item?.notes),
      payment: {
        buyingPrice: toText(item?.buying_price),
        sellingPrice: toText(item?.selling_price),
        paymentTerm: toText(item?.payment_terms),
        billingFrequency: toText(item?.billing_frequency),
        paymentMode: toText(item?.payment_method),
        bank: toText(item?.bank_name),
        accountTitle: toText(item?.account_title),
        iban: toText(item?.iban_number),
      },
      shareWith: {
        email: Boolean(notifyConfig?.email),
        whatsapp: Boolean(notifyConfig?.whatsapp),
      },
      isInsured: Boolean(item?.insured),
    },
  };
};

const actionMeta = (status) => {
  const normalized = toStatusKey(status);

  if (normalized === "inactive") {
    return {
      label: "Set Active",
      icon: <PiCircleFill />,
      color: "#EF0000",
      disabled: false,
    };
  }

  if (normalized === "active") {
    return {
      label: "Set In Transit",
      icon: <PiPlayFill />,
      color: "#1447B1",
      disabled: false,
    };
  }

  if (normalized === "in_transit") {
    return {
      label: "Set Completed",
      icon: <PiStopFill />,
      color: "#F2841B",
      disabled: false,
    };
  }

  if (normalized === "delayed") {
    return {
      label: "Complete Delayed Trip",
      icon: <MdOutlineAccessTime />,
      color: "#9C9FC9",
      disabled: false,
    };
  }

  return {
    label: "Completed",
    icon: <PiCheckCircleFill />,
    color: "#299900",
    disabled: true,
  };
};

const nextStatus = (status) => {
  const normalized = toStatusKey(status);
  if (normalized === "inactive") return "active";
  if (normalized === "active") return "in_transit";
  if (normalized === "in_transit") return "completed";
  return normalized;
};

const loadBookingStatusMeta = (status) => {
  const normalized = toStatusKey(status);

  if (normalized === "active") {
    return {
      color: "#299900",
      icon: (
        <img
          src={activeLoadStatusIcon}
          alt="Active status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Active",
    };
  }

  if (normalized === "inactive") {
    return {
      color: "#EF0000",
      icon: (
        <img
          src={inactiveStatusIcon}
          alt="Inactive status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Inactive",
    };
  }

  if (normalized === "prospect") {
    return {
      color: "#F2841B",
      icon: (
        <img
          src={prospectStatusIcon}
          alt="Prospect status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Prospect",
    };
  }

  if (normalized === "in_transit") {
    return {
      color: "#F2841B",
      icon: (
        <img
          src={inTransitLoadStatusIcon}
          alt="In transit status"
          className="transporter_status_icon_img"
        />
      ),
      label: "In Transit",
    };
  }

  if (normalized === "delayed") {
    return {
      color: "#9C9FC9",
      icon: (
        <img
          src={delayedStatsIcon}
          alt="Delayed status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Delayed",
    };
  }

  if (normalized === "completed") {
    return {
      color: "#299900",
      icon: (
        <img
          src={activeStatusIcon}
          alt="Completed status"
          className="transporter_status_icon_img"
        />
      ),
      label: "Completed",
    };
  }

  return {
    color: "#8F95B2",
    icon: null,
    label: titleCase(status),
  };
};

const LoadBooking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    loadBookingOverviewData,
    loadBookingPaginatedData,
    loadBookingPaginatedLoading,
  } = useSelector((state) => state.loadBooking);
  const { transporterCitiesDropdownData = [] } = useSelector(
    (state) => state.dropdowns
  );

  const [allRows, setAllRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [delayModalOpen, setDelayModalOpen] = useState(false);
  const [delayTargetId, setDelayTargetId] = useState(null);
  const [delayForm, setDelayForm] = useState({
    deliveryDate: "",
    deliveryTime: "",
    reason: "",
  });
  const apiSearchFilters = useMemo(
    () => buildLoadBookingSearchFilters(searchFilters),
    [searchFilters]
  );

  useEffect(() => {
    dispatch(fetchLoadBookingOverview());
    dispatch(fetchTransporterCitiesDropdown());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  useEffect(() => {
    dispatch(
      fetchLoadBookingsPaginated({
        page: currentPage,
        limit,
        search_text: searchText,
        searchFilters: apiSearchFilters,
      })
    );
  }, [dispatch, currentPage, limit, searchText, apiSearchFilters]);

  const locationLookup = useMemo(() => {
    const source = safeArray(transporterCitiesDropdownData).length
      ? transporterCitiesDropdownData
      : locationOptions;

    return buildLocationLookup(source);
  }, [transporterCitiesDropdownData]);

  const apiRows = useMemo(
    () =>
      safeArray(loadBookingPaginatedData?.results).map((item, index) =>
        toLoadBookingRow(item, index, locationLookup)
      ),
    [loadBookingPaginatedData, locationLookup]
  );

  useEffect(() => {
    setAllRows(apiRows);
  }, [apiRows]);

  const rows = useMemo(() => allRows, [allRows]);

  const totalPages = toPositiveNumber(loadBookingPaginatedData?.meta?.totalPages, 1);
  const totalItems = (() => {
    const metaCount = Number(loadBookingPaginatedData?.meta?.totalItems);
    if (Number.isFinite(metaCount) && metaCount >= 0) return metaCount;
    const total = Number(loadBookingPaginatedData?.total);
    if (Number.isFinite(total) && total >= 0) return total;
    return rows.length;
  })();

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [currentPage, totalPages]);

  const selectedDeleteRow = useMemo(
    () => rows.find((item) => item.id === deleteId) || null,
    [rows, deleteId]
  );

  const selectedDeleteName = selectedDeleteRow?.id || "this record";
  const deleteTitle = "Are you sure you want to delete this load booking?";
  const deleteDescription = `Deleting "${selectedDeleteName}" will permanently remove this load booking from your records. This action cannot be undone.`;

  const closeDeletePopup = () => {
    if (deleteLoading) return;
    setDeletePopup(false);
    setDeleteId(null);
  };

  const closeDelayModal = () => {
    setDelayModalOpen(false);
    setDelayTargetId(null);
    setDelayForm({
      deliveryDate: "",
      deliveryTime: "",
      reason: "",
    });
  };

  const localStats = useMemo(() => {
    const total = totalItems || allRows.length;
    const active = allRows.filter((item) => toStatusKey(item.status) === "active").length;
    const inactive = allRows.filter(
      (item) => toStatusKey(item.status) === "inactive"
    ).length;
    const inTransit = allRows.filter(
      (item) => toStatusKey(item.status) === "in_transit"
    ).length;
    const delayed = allRows.filter((item) => toStatusKey(item.status) === "delayed").length;
    const completed = allRows.filter(
      (item) => toStatusKey(item.status) === "completed"
    ).length;
    const last = allRows
      .map((item) => item.updatedAt)
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    return { total, active, inactive, inTransit, delayed, completed, last };
  }, [allRows, totalItems]);

  const getMetric = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const totalCount = getMetric(loadBookingOverviewData?.total_loads, localStats.total);
  const activeCount = getMetric(loadBookingOverviewData?.active?.count, localStats.active);
  const inactiveCount = getMetric(
    loadBookingOverviewData?.inactive?.count,
    localStats.inactive
  );
  const inTransitCount = getMetric(
    loadBookingOverviewData?.in_transit?.count,
    localStats.inTransit
  );
  const delayedCount = getMetric(
    loadBookingOverviewData?.delayed?.count,
    localStats.delayed
  );
  const completedCount = getMetric(
    loadBookingOverviewData?.completed?.count,
    localStats.completed
  );

  const getPercentage = (value, fallbackPart, fallbackTotal) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return `${parsed.toFixed(1)}%`;
    return percent(fallbackPart, fallbackTotal);
  };

  const activePercentage = getPercentage(
    loadBookingOverviewData?.active?.percentage,
    activeCount,
    totalCount
  );
  const inactivePercentage = getPercentage(
    loadBookingOverviewData?.inactive?.percentage,
    inactiveCount,
    totalCount
  );
  const inTransitPercentage = getPercentage(
    loadBookingOverviewData?.in_transit?.percentage,
    inTransitCount,
    totalCount
  );
  const delayedPercentage = getPercentage(
    loadBookingOverviewData?.delayed?.percentage,
    delayedCount,
    totalCount
  );
  const completedPercentage = getPercentage(
    loadBookingOverviewData?.completed?.percentage,
    completedCount,
    totalCount
  );
  const lastUpdatedAt = loadBookingOverviewData?.last_updated || localStats.last;

  const statsData = [
    {
      title: "Total Loads",
      value: totalCount,
      text: `Last updated : ${lastUpdatedAt ? dateMonthYear(lastUpdatedAt) : "--"}`,
      icon: (
        <img src={totalLoadsStatsIcon} alt="Total loads icon" width={46} height={46} />
      ),
    },
    {
      title: "Active",
      value: activeCount,
      text: (
        <span>
          <b>{activePercentage}</b> from total
        </span>
      ),
      icon: activeStatsIcon,
      className: "completed",
    },
    {
      title: "Inactive",
      value: inactiveCount,
      text: (
        <span>
          <b>{inactivePercentage}</b> from total
        </span>
      ),
      icon: lateStatsIcon,
      className: "late",
    },
    {
      title: "In Transit",
      value: inTransitCount,
      text: (
        <span>
          <b>{inTransitPercentage}</b> from total
        </span>
      ),
      icon: (
        <img src={inTransitStatsIcon} alt="In transit icon" width={46} height={46} />
      ),
      className: "pending-delivery",
    },
    {
      title: "Delayed",
      value: delayedCount,
      text: (
        <span>
          <b>{delayedPercentage}</b> from total
        </span>
      ),
      icon: <img src={delayedStatsIcon} alt="Delayed icon" width={46} height={46} />,
      className: "muted-load-stat",
    },
    {
      title: "Completed",
      value: completedCount,
      text: (
        <span>
          <b>{completedPercentage}</b> from total
        </span>
      ),
      icon: statsCompletedIcon,
      className: "completed",
    },
  ];

  const customerFilterOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((item) => {
      if (!item.customerName) return;
      const key = normalize(item.customerName);
      if (!map.has(key)) {
        map.set(key, { label: item.customerName, value: item.customerName });
      }
    });
    return Array.from(map.values());
  }, [allRows]);

  const transporterFilterOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((item) => {
      if (!item.transporterName) return;
      const key = normalize(item.transporterName);
      if (!map.has(key)) {
        map.set(key, { label: item.transporterName, value: item.transporterName });
      }
    });
    return Array.from(map.values());
  }, [allRows]);

  const locationFilterOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((item) => {
      safeArray([item.loadingPoint, item.offloadingPoint]).forEach((point) => {
        if (!point) return;
        const key = normalize(point);
        if (!map.has(key)) {
          map.set(key, { label: point, value: point });
        }
      });
    });
    return Array.from(map.values());
  }, [allRows]);

  const handlePrimaryAction = (row) => {
    const normalized = toStatusKey(row?.status);

    if (normalized === "completed") return;

    if (normalized === "delayed") {
      setDelayTargetId(row.id);
      setDelayModalOpen(true);
      return;
    }

    const updatedRows = allRows.map((item) =>
      item.id === row.id
        ? {
            ...item,
            status: nextStatus(item.status),
            updatedAt: new Date().toISOString(),
          }
        : item
    );

    setAllRows(updatedRows);
    successNotify("Load status updated successfully");
  };

  const handleDelete = () => {
    if (!deleteId || deleteLoading) return;

    setDeleteLoading(true);
    const nextRows = allRows.filter((item) => item.id !== deleteId);
    setAllRows(nextRows);
    successNotify("Load booking deleted successfully");

    if (rows.length === 1 && currentPage > 1) {
      setCurrentPage((previous) => Math.max(1, previous - 1));
    }

    setDeleteLoading(false);
    closeDeletePopup();
  };

  const canUpdateDelay = Boolean(
    delayForm.deliveryDate && delayForm.deliveryTime && stripHtml(delayForm.reason)
  );

  const handleDelayedComplete = () => {
    if (!delayTargetId || !canUpdateDelay) return;

    const updatedRows = allRows.map((item) => {
      if (item.id !== delayTargetId) return item;

      const updatedFormData = {
        ...(item.formData || {}),
        deliveryDateTime: `${delayForm.deliveryDate}T${delayForm.deliveryTime}`,
        notes: delayForm.reason,
      };

      return {
        ...item,
        status: "completed",
        expectedDeliveryDate: delayForm.deliveryDate,
        updatedAt: new Date().toISOString(),
        formData: updatedFormData,
      };
    });

    setAllRows(updatedRows);
    closeDelayModal();
    successNotify("Delayed trip completed successfully");
  };

  const columns = [
    {
      name: "Load ID",
      key: "id",
      selector: (row) => (
        <span className="hyperlink" style={{ textTransform: "uppercase" }}>
          {row.id}
        </span>
      ),
      tableColumn: true,
      filterName: "idFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "110px",
    },
    {
      name: "Customer Name",
      key: "customerName",
      selector: (row) => (
        <span className="hyperlink" style={{ textTransform: "capitalize" }}>
          {row.customerName || "-"}
        </span>
      ),
      tableColumn: true,
      filterName: "customerNameFilter",
      type: "select",
      options: customerFilterOptions,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "190px",
    },
    {
      name: "Transporter Name",
      key: "transporterName",
      selector: (row) =>
        row.transporterName ? (
          <span className="hyperlink">{row.transporterName}</span>
        ) : (
          <button type="button" className="load_booking_assign_btn">
            Select Transporter
          </button>
        ),
      tableColumn: true,
      filterName: "transporterNameFilter",
      type: "select",
      options: transporterFilterOptions,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "180px",
    },
    {
      name: "Loading Point",
      key: "loadingPoint",
      selector: (row) => {
        const point = toText(row.loadingPoint);
        const hasPoint = point && point !== "-";

        if (!hasPoint) return "-";

        return (
          <span className="load_booking_point_cell">
            <IoLocationSharp color="#999999" size={14} />
            <MultiLineText text={point} maxWidth="145px" />
          </span>
        );
      },
      tableColumn: true,
      filterName: "loadingPointFilter",
      type: "select",
      options: locationFilterOptions,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "170px",
    },
    {
      name: "Offloading Point",
      key: "offloadingPoint",
      selector: (row) => {
        const point = toText(row.offloadingPoint);
        const hasPoint = point && point !== "-";

        if (!hasPoint) return "-";

        return (
          <span className="load_booking_point_cell">
            <IoLocationSharp color="#999999" size={14} />
            <MultiLineText text={point} maxWidth="145px" />
          </span>
        );
      },
      tableColumn: true,
      filterName: "offloadingPointFilter",
      type: "select",
      options: locationFilterOptions,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "170px",
    },
    {
      name: "Expected Delivery Date",
      key: "expectedDeliveryDate",
      selector: (row) => row.expectedDeliveryDate || "-",
      tableColumn: true,
      filterName: "expectedDeliveryDateFilter",
      type: "text",
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "165px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => {
        const meta = loadBookingStatusMeta(row.status);

        return (
          <span className="transporter_status_cell" style={{ color: meta.color }}>
            {meta.icon}
            {meta.label}
          </span>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      type: "select",
      options: statusFilterOptions,
      checked: true,
      disabled: true,
      hide: false,
      exportColumn: true,
      sortable: false,
      minWidth: "125px",
    },
    {
      name: "Action",
      key: "actions",
      selector: (row) => {
        const action = actionMeta(row.status);
        return (
          <div className="table_action_btn load_booking_actions">
            <button
              type="button"
              title={action.label}
              style={{ color: action.color }}
              disabled={action.disabled}
              onClick={() => handlePrimaryAction(row)}
            >
              {action.icon}
            </button>
            <span className="load_booking_action_divider" />
            <button
              type="button"
              className="edit_btn"
              title="Edit"
              onClick={() => {
                const recordId = row?.recordId || row?.id;
                navigate(`/3pl/load-booking/details/${recordId}`, {
                  state: { mode: "edit", id: recordId, record: row },
                });
              }}
            >
              <PiPencilSimpleLine />
            </button>
            <button
              type="button"
              className="delete_btn"
              title="Delete"
              onClick={() => {
                setDeleteId(row.id);
                setDeletePopup(true);
              }}
            >
              <PiTrash />
            </button>
          </div>
        );
      },
      checked: true,
      disabled: false,
      hide: false,
      exportColumn: false,
      sortable: false,
      tableColumn: false,
      minWidth: "130px",
      grow: 0,
    },
  ];

  return (
    <div>
      <Breadcrumbs values={["Load Booking"]} />

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={closeDeletePopup}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title={deleteTitle}
        description={deleteDescription}
      />

      <Modal
        show={delayModalOpen}
        onHide={closeDelayModal}
        centered
        backdrop="static"
        dialogClassName="delay_trip_modal_dialog"
        contentClassName="delay_trip_modal"
      >
        <Modal.Body className="delay_trip_modal_body">
          <div className="delay_trip_modal_header">
            <h4>Complete Delayed Trip</h4>
            <button type="button" onClick={closeDelayModal}>
              <MdClose />
            </button>
          </div>

          <p className="delay_trip_modal_description">
            To complete the trip, please enter the reason for the delay.
          </p>

          <div className="delay_trip_modal_grid">
            <Input
              label="New Delivery Date"
              placeholder="YY-MM-DD"
              type="date"
              name="deliveryDate"
              value={delayForm.deliveryDate}
              onChange={(event) =>
                setDelayForm((previous) => ({
                  ...previous,
                  deliveryDate: event.target.value,
                }))
              }
              onBlur={() => {}}
              showIcon={<MdOutlineCalendarMonth size={34} color="#3D3F5C" />}
              required={false}
            />
            <Input
              label="New Delivery Time"
              placeholder="HH:MM"
              type="time"
              name="deliveryTime"
              value={delayForm.deliveryTime}
              onChange={(event) =>
                setDelayForm((previous) => ({
                  ...previous,
                  deliveryTime: event.target.value,
                }))
              }
              onBlur={() => {}}
              showIcon={<MdAccessTime size={34} color="#3D3F5C" />}
              required={false}
            />
          </div>

          <div className="input_wrapper">
            <div className="input_container delay_quill_wrapper">
              <label>Reason of Delay</label>
              <ReactQuill
                placeholder="Write the delay reason..."
                modules={modules}
                formats={formats}
                theme="snow"
                value={delayForm.reason}
                onChange={(content) =>
                  setDelayForm((previous) => ({ ...previous, reason: content }))
                }
                className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
              />
            </div>
          </div>

          <div className="delay_trip_modal_actions">
            <button type="button" className="cancel_btn" onClick={closeDelayModal}>
              Cancel
            </button>
            <button
              type="button"
              className="main_btn"
              disabled={!canUpdateDelay}
              onClick={handleDelayedComplete}
            >
              Update
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <div className="access_view">
        <h2>Load Booking</h2>

        <div className="services_buttons">
          <button onClick={() => navigate("/3pl/load-booking/create")}>
            + Create Load
          </button>
        </div>
      </div>

      <Stats statsData={statsData} numberOfColumn={2} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={rows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={loadBookingPaginatedLoading}
        paginationLoading={loadBookingPaginatedLoading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        tableClassName="transporters_table"
      />

      {!loadBookingPaginatedLoading && !rows.length && (
        <div className="add_trip_suggestion_div" style={{ marginTop: 0 }}>
          {addTripIcon}
          <h4>There is nothing here...</h4>
          <p>
            There is nothing to view right now, please create a load booking data to
            get started.
          </p>
          <Button
            submitText="Add Load"
            type="button"
            shouldRow={false}
            isShowSubmitBtn
            isShowCancel={false}
            submitBtn={() => navigate("/3pl/load-booking/create")}
          />
        </div>
      )}
    </div>
  );
};

export default LoadBooking;
